













































import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { vxm } from '@/store'

@Component
export default class SupplierBrandSelector extends Vue {
  @Prop({ default: false }) readonly!: boolean
  @Prop({ required: true }) supplier!: number | null
  @Prop({ required: true }) brand!: number | null
  @Prop({ required: true }) availableSuppliers!: any[]
  @Prop({ default: false }) loadingSuppliers!: boolean
  @Prop({ default: () => [] }) usedSupplierBrands!: Array<{ supplier: number; brand: number }>

  private localSupplier: number | null = null
  private localBrand: number | null = null
  private availableBrands: any[] = []
  private loadingBrands = false
  private brandSearchQuery = ''
  private searchTimeout: number | null = null

  public mounted(): void {
    this.localSupplier = this.supplier
    this.localBrand = this.brand

    // If we have a pre-loaded brand ID but no brand data, fetch it
    if (this.localBrand && this.localSupplier && this.availableBrands.length === 0) {
      this.loadBrandById(this.localBrand)
    }
  }

  public beforeDestroy(): void {
    this.clearSearchTimeout()
  }

  @Watch('supplier')
  private onSupplierPropChange(newVal: number | null): void {
    this.localSupplier = newVal
  }

  @Watch('brand')
  private onBrandPropChange(newVal: number | null): void {
    this.localBrand = newVal

    // If we have a brand ID but no brand data, fetch it
    if (this.localBrand && this.localSupplier && !this.availableBrands.find((b) => b.id === this.localBrand)) {
      this.loadBrandById(this.localBrand)
    }
  }

  private onSupplierChange(): void {
    this.localBrand = null
    this.availableBrands = []
    this.brandSearchQuery = ''
    this.clearSearchTimeout()
    this.$emit('supplier-change', this.localSupplier)
  }

  private onBrandChange(): void {
    this.$emit('brand-change', this.localBrand)
  }

  private onBrandSearch(searchQuery: string): void {
    if (!this.localSupplier) return

    this.brandSearchQuery = searchQuery

    if (!searchQuery || searchQuery.length < 2) {
      this.availableBrands = []
      this.clearSearchTimeout()
      return
    }

    // Clear existing timeout
    this.clearSearchTimeout()

    // Set new timeout for 300ms
    this.searchTimeout = window.setTimeout(() => {
      this.performBrandSearch(searchQuery)
    }, 300)
  }

  private clearSearchTimeout(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  }

  private performBrandSearch(searchQuery: string): void {
    this.loadingBrands = true
    this.$axios
      .get(
        `/v4/site/products/brands/search?supplierIds[]=${this.localSupplier}&query=${encodeURIComponent(
          searchQuery,
        )}`,
      )
      .then((response) => {
        this.availableBrands = response.data.data || []
        this.loadingBrands = false
      })
      .catch((err) => {
        vxm.alert.onAxiosError(err, this.$t('Failed to search brands'))
        this.loadingBrands = false
      })
  }

  private loadBrandById(brandId: number): void {
    if (!brandId) return

    this.loadingBrands = true
    this.$axios
      .get(`/v4/site/products/brands/search?id=${brandId}`)
      .then((response) => {
        const brandData = response.data.data
        if (brandData && brandData.length > 0) {
          const brand = brandData[0]
          // Add the brand to availableBrands if not already present
          if (!this.availableBrands.find((b) => b.id === brand.id)) {
            this.availableBrands.push(brand)
          }
        }
        this.loadingBrands = false
      })
      .catch((err) => {
        vxm.alert.onAxiosError(err, this.$t('Failed to load brand details'))
        this.loadingBrands = false
      })
  }

  private get supplierRules(): Array<(v: any) => boolean | string> {
    return [(v: any) => !!v || this.$t('Supplier is required')]
  }

  private get brandRules(): Array<(v: any) => boolean | string> {
    return [
      (v: any) => !!v || this.$t('Brand is required'),
      (v: any) =>
        !this.isSupplierBrandCombinationUsed(this.localSupplier, v) ||
        this.$t('This supplier-brand combination is already used'),
    ]
  }

  private isSupplierBrandCombinationUsed(supplierId: number | null, brandId: number | null): boolean {
    if (!supplierId || !brandId) return false

    return this.usedSupplierBrands.some(
      (combination) => combination.supplier === supplierId && combination.brand === brandId,
    )
  }

  private get filteredAvailableSuppliers(): any[] {
    // For suppliers, we don't filter anything since suppliers can be reused
    // The constraint is on supplier-brand combinations, not suppliers alone
    return this.availableSuppliers
  }
}
